import React, { useCallback, useState } from "react";
import { toast } from "react-toastify";

import { useAuth } from "../../hooks/pages/useAuth";

import { usePropertyManagement } from "../../hooks/admin/property/usePropertyManagement";

import {
  PROPERTY_INITIAL_FORM,
} from "../../constants/admin/property/propertyConstants";

import {
  createPropertyFormData,
  buildPropertyPayload,
} from "../../utils/admin/propertyValidation";

import PropertyForm from "../../components/admin/property/PropertyForm";
import PropertyTable from "../../components/admin/property/PropertyTable";

const ManageProperties = () => {
  const { user: currentUser } = useAuth();

  const {
    properties,
    projects,

    loading,

    filters,
    setFilters,

    page,
    setPage,

    totalPages,

    create,
    update,
    remove,
    uploadPropertyImages,
  } = usePropertyManagement();

  const [showForm, setShowForm] = useState(false);

  const [editingProperty, setEditingProperty] =
    useState(null);

  const [formData, setFormData] = useState(
    PROPERTY_INITIAL_FORM
  );

  const [imageFiles, setImageFiles] = useState([]);

  const [isSubmitting, setIsSubmitting] =
    useState(false);

  const closeModal = useCallback(() => {
    setShowForm(false);
    setEditingProperty(null);
    setImageFiles([]);
    setFormData(PROPERTY_INITIAL_FORM);
  }, []);

  const handleCreate = useCallback(() => {
    setEditingProperty(null);
    setImageFiles([]);
    setFormData(PROPERTY_INITIAL_FORM);
    setShowForm(true);
  }, []);

  const handleEdit = useCallback((property) => {
    setEditingProperty(property);

    setFormData(
      createPropertyFormData(property)
    );

    setImageFiles([]);

    setShowForm(true);
  }, []);

  const handleDelete = useCallback(
    async (id) => {
      const confirmed = window.confirm(
        "Bạn có chắc chắn muốn xoá sản phẩm này không?"
      );

      if (!confirmed) return;

      try {
        await remove(id);

        toast.success(
          "Xóa sản phẩm thành công!"
        );
      } catch (error) {
        console.error(error);

        toast.error(
          "Không thể xoá sản phẩm"
        );
      }
    },
    [remove]
  );

  const handleChange = useCallback((e) => {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : value,
    }));
  }, []);

  const handleImageChange = useCallback((e) => {
    const files = Array.from(
      e.target.files || []
    );

    setImageFiles((prev) => [
      ...prev,
      ...files,
    ]);
  }, []);

  const handleRemoveImage = useCallback(
    (index) => {
      setImageFiles((prev) =>
        prev.filter((_, i) => i !== index)
      );
    },
    []
  );

  const buildPayload = async () => {
    let uploadedImages = [];
  
    if (imageFiles.length > 0) {
      const uploadResult =
        await uploadPropertyImages(
          imageFiles
        );
  
      uploadedImages =
        uploadResult.urls || [];
    }
  
    const payload =
      buildPropertyPayload({
        formData,
        uploadedImages,
      });
    return payload;
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      const payload =
        await buildPayload();

      if (editingProperty) {
        await update(
          editingProperty.id,
          payload
        );

        toast.success(
          "Cập nhật sản phẩm thành công!"
        );
      } else {
        await create(payload);

        toast.success(
          "Tạo sản phẩm mới thành công!"
        );
      }

      closeModal();
    } catch (error) {
      console.error(error);

      toast.error(
        error?.message ||
          "Không thể lưu sản phẩm"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFilterChange = useCallback(
    (e) => {
      const { name, value } = e.target;

      setFilters((prev) => ({
        ...prev,
        [name]: value,
      }));
    },
    [setFilters]
  );

  const handleResetFilters =
    useCallback(() => {
      setFilters({});
    }, [setFilters]);

  return (
    <div className="p-6 space-y-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-blue-700 dark:text-blue-400">
          Quản lý sản phẩm bất động sản
        </h1>

        {currentUser?.role === "admin" && (
          <button
            onClick={handleCreate}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
          >
            Đăng sản phẩm mới
          </button>
        )}
      </div>

      {/* Filter */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-100 dark:bg-gray-800 p-4 rounded">
        <input
          type="text"
          name="projectName"
          placeholder="Tên dự án"
          value={filters.projectName || ""}
          onChange={handleFilterChange}
          className="p-2 border rounded"
        />

        <select
          name="propertyType"
          value={filters.propertyType || ""}
          onChange={handleFilterChange}
          className="p-2 border rounded"
        >
          <option value="">
            Loại bất động sản
          </option>

          <option value="apartment">
            Căn hộ
          </option>

          <option value="villa">
            Biệt thự
          </option>

          <option value="shophouse">
            Shophouse
          </option>

          <option value="condotel">
            Condotel
          </option>

          <option value="penthouse">
            Penthouse
          </option>

          <option value="duplex">
            Duplex
          </option>
        </select>

        <select
          name="status"
          value={filters.status || ""}
          onChange={handleFilterChange}
          className="p-2 border rounded"
        >
          <option value="">
            Trạng thái duyệt
          </option>

          <option value="pending">
            Chờ duyệt
          </option>

          <option value="approved">
            Đã duyệt
          </option>

          <option value="rejected">
            Từ chối
          </option>
        </select>

        <button
          onClick={handleResetFilters}
          className="bg-gray-300 hover:bg-gray-400 px-4 py-2 rounded"
        >
          Đặt lại
        </button>
      </div>

      {loading ? (
        <p>Đang tải dữ liệu...</p>
      ) : (
        <PropertyTable
          properties={properties}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      {/* Modal */}
      {showForm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm overflow-y-auto">
          <div className="relative w-full max-w-6xl bg-white dark:bg-gray-900 rounded-lg shadow-xl mx-4">
            <button
              onClick={closeModal}
              className="absolute top-3 right-4 text-2xl"
            >
              &times;
            </button>

            <div className="max-h-[90vh] overflow-y-auto p-6">
              <PropertyForm
                formData={formData}
                handleChange={handleChange}
                handleImageChange={
                  handleImageChange
                }
                handleRemoveImage={
                  handleRemoveImage
                }
                handleSubmit={handleSubmit}
                editingProperty={
                  editingProperty
                }
                imageFiles={imageFiles}
                projects={projects}
                isSubmitting={
                  isSubmitting
                }
                onClose={closeModal}
              />
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      <div className="flex justify-center items-center gap-2">
        <button
          disabled={page === 1}
          onClick={() =>
            setPage((prev) =>
              Math.max(prev - 1, 1)
            )
          }
          className="px-3 py-1 border rounded disabled:opacity-50"
        >
          ← Trước
        </button>

        <span>
          Trang {page} / {totalPages}
        </span>

        <button
          disabled={page === totalPages}
          onClick={() =>
            setPage((prev) =>
              Math.min(
                prev + 1,
                totalPages
              )
            )
          }
          className="px-3 py-1 border rounded disabled:opacity-50"
        >
          Sau →
        </button>
      </div>
    </div>
  );
};

export default ManageProperties;